import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import Exporting from "highcharts/modules/exporting";
import OfflineExporting from "highcharts/modules/offline-exporting";

Exporting(Highcharts);
OfflineExporting(Highcharts);

interface SeriesInput {
  name: string;
  data: (number | null)[];
}

interface AnalyticsSplineChartProps {
  seriesData: SeriesInput[];
  yAxisTitleText?: string;
  fileName?: string;
  chartName?: string;
  xAxisCategories?: string[];
  xAxisTitle?: string;
}

// Default 24 hours (1-hour intervals)
const HOURS_24 = Array.from({ length: 24 }, (_, i) => `${i}.00`);

// 15-minute intervals from 0:00 to 23:45 (96 intervals)
const MINUTES_15_INTERVALS = Array.from({ length: 96 }, (_, i) => {
  const hour = Math.floor(i / 4);
  const minute = (i % 4) * 15;
  return `${hour}:${minute.toString().padStart(2, "0")}`;
});

function generateColors(n: number) {
  // Generate visually distinct pastel-ish HSL colors
  const colors: string[] = [];
  for (let i = 0; i < n; i++) {
    const hue = Math.round((360 / Math.max(1, n)) * i) % 360;
    const sat = 70; // saturation
    const light = 55; // lightness
    colors.push(`hsl(${hue} ${sat}% ${light}%)`);
  }
  return colors;
}

export { HOURS_24, MINUTES_15_INTERVALS };

const AnalyticsSplineChart: FC<AnalyticsSplineChartProps> = ({
  seriesData,
  yAxisTitleText = "Motion Count",
  fileName,
  chartName,
  xAxisCategories,
  xAxisTitle = "Time(Hours)",
}) => {
  const chartComponentRef = useRef<HighchartsReact.RefObject>(null);
  const [yAxisMin, setYAxisMin] = useState<number>(0);
  const [yAxisMax, setYAxisMax] = useState<number>(50);

  const safeXAxisCategories = useMemo(
    () => [...(xAxisCategories || HOURS_24)],
    [xAxisCategories],
  );

  // Build series with enforced type/showInLegend and generated colors
  const builtSeries = useMemo(() => {
    const colors = generateColors(seriesData?.length || 0);
    return (seriesData || []).map((s, idx) => ({
      type: "spline" as const,
      data: [...(s.data || [])],
      showInLegend: true,
      name: s.name,
      color: colors[idx],
    }));
  }, [seriesData]);

  useEffect(() => {
    const numericPoints = (seriesData || [])
      .flatMap((series) => series.data || [])
      .filter((point): point is number => typeof point === "number");

    if (numericPoints.length) {
      let min = Math.min(...numericPoints) - 5;
      let max = Math.max(...numericPoints) + 5;
      if (min < 0) min = 0;
      setYAxisMin(min);
      setYAxisMax(max);
    } else {
      setYAxisMin(0);
      setYAxisMax(50);
    }

    // Reset zoom when data changes
    if (chartComponentRef?.current?.chart) {
      chartComponentRef?.current?.chart?.zoomOut();
    }
  }, [seriesData]);

  const chartOptions: Highcharts.Options = {
    chart: {
      style: {
        fontFamily: '"Cabin", Helvetica, Arial, sans-serif',
      },
      zooming: {
        type: "x",
      },
      animation: {
        duration: 1200,
      },
    },
    lang: { noData: "No data available" },
    title: { text: "" },
    credits: { enabled: false },
    xAxis: {
      categories: safeXAxisCategories,
      title: { text: xAxisTitle },
      crosshair: true,
    },
    yAxis: {
      min: yAxisMin,
      max: yAxisMax,
      title: { text: yAxisTitleText },
      labels: { format: "{value}" },
      // crosshair: true,
    },
    tooltip: {
      shared: true,
      // formatter: function (this: Highcharts.TooltipFormatterContextObject) {
      //   const xValue = this.x;
      //   const yValue = this.y;
      //   return `\n        <strong>Time:</strong> ${xValue}<br><strong>${yAxisTitleText}:</strong> ${yValue}\n      `;
      // },
    },
    plotOptions: {
      spline: {
        connectNulls: true,
        marker: {
          symbol: "circle",
          radius: 4,
          lineColor: "#666666",
          lineWidth: 1,
        },
      },
    },
    legend: { enabled: true, x: 40, y: 0 },
    exporting: {
      enabled: true,
      fallbackToExportServer: false,
      filename: fileName || "chart",
      sourceWidth: 1200,
      sourceHeight: 500,
      scale: 1,
      chartOptions: chartName
        ? {
            chart: {
              height: "100%",
              width: "100%",
              spacingTop: 30,
              marginTop: 100,
            },
            title: {
              text: chartName,
              align: "center",
              style: { fontSize: "24px", fontWeight: "bold" },
            },
          }
        : { chart: { height: "100%", width: "100%" } },
      buttons: {
        contextButton: {
          menuItems: [
            "viewFullscreen",
            "printChart",
            "separator",
            "downloadPNG",
            "downloadJPEG",
            "downloadPDF",
            "downloadSVG",
          ],
        },
      },
    },
    series: builtSeries as Highcharts.SeriesOptionsType[],
  };

  return (
    <div>
      {builtSeries && builtSeries.length ? (
        <HighchartsReact
          highcharts={Highcharts}
          options={chartOptions}
          ref={chartComponentRef}
        />
      ) : (
        <div
          style={{
            height: "300px",
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "#D9D9D9",
            fontSize: "22px",
            fontWeight: "600",
            color: "#6d6d6d",
          }}
        >
          No Data Available
        </div>
      )}
    </div>
  );
};

export default React.memo(AnalyticsSplineChart);
